import CardLinkForm from './CardLinkForm'
import { useEffect, useState } from 'react'
import CustomIFrame from '@/src/card-linking/components/CustomIFrame'
import usePostCardLinkSession from '@/src/card-linking/hooks/usePostCardLinkSession'
import CardLinkLoader from '@/src/card-linking/components/CardLinkLoader'
import usePostGiftRedeemCardLink from '@/src/common/hooks/usePostGiftRedeemCardLink'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import { TCardLinkFunctions } from '@/src/card-linking/types'
import { UseMutationResult } from 'react-query'

interface ICardLinkingAppProps {
    functions: TCardLinkFunctions
    deleteCard: UseMutationResult<any, any, any, any>
    cardToCardUuid: string
    hideGiftDetailsButton?: boolean
    pushStepOnActivate?: boolean
    initialStep?: 'INITIAL' | 'REMOVE_OR_ADD_CARDS'
}

export default function CardLinkApp({
    functions,
    hideGiftDetailsButton,
    initialStep,
    deleteCard,
    cardToCardUuid,
}: ICardLinkingAppProps) {
    const { giftState } = usePGPlusStepAndModalContext()
    const { cardUuid } = giftState
    const [windowEventData, setWindowEventData] = useState<any>(null)
    const [cardLinkSessionId, setCardLinkSessionId] = useState('')

    const {
        mutate: createCardLinkSession,
        isLoading: isLoadingCardLinkSession,
        isError: isErrorCardLinkSession,
    } = usePostCardLinkSession()

    const { mutate: createGiftRedeemCardLink, isSuccess: giftLinkIsSuccess } =
        usePostGiftRedeemCardLink()

    // This hook is listening for events that come from the iframe
    useEffect(() => {
        const handler = (ev: any) => {
            if (ev.type !== 'message' || !ev.data) {
                return
            }
            // @TODO add a better filter so that vercel window events aren't caught here
            try {
                setWindowEventData(JSON.parse(ev.data))
            } catch (e) {}
        }

        window.addEventListener('message', handler)

        // Don't forget to remove addEventListener
        return () => window.removeEventListener('message', handler)
    }, [])

    // Listen for windowEventData and send post request to create a pending card link action
    useEffect(() => {
        if (
            windowEventData &&
            windowEventData?.message === 'card_link_complete'
        ) {
            createGiftRedeemCardLink({
                card_uuid: cardUuid,
                gyp_redemption_account_id:
                    windowEventData.redemption_account_id,
                card_last_four: windowEventData.last_four,
                card_type: windowEventData.card_type,
            })
        }
    }, [cardUuid, createGiftRedeemCardLink, windowEventData])

    useEffect(() => {
        createCardLinkSession(
            {
                card_uuid: cardUuid,
            },
            {
                onSuccess: (data) => {
                    setCardLinkSessionId(data.session_id)
                },
                onError: (error) => {
                    console.error('createCardLinkSession onError', error)
                },
            }
        )
    }, [cardUuid, createCardLinkSession])

    useEffect(() => {
        if (giftLinkIsSuccess) {
            functions?.refetchGift && functions.refetchGift()
            functions?.handleStepChange && functions.handleStepChange()
        }
    }, [functions, giftLinkIsSuccess])

    if (isLoadingCardLinkSession) {
        return <CardLinkLoader />
    }

    if (isErrorCardLinkSession) {
        return <p>An error occurred. Please refresh the page and try again.</p>
    }

    if (isLoadingCardLinkSession) {
        return (
            <div>
                <CardLinkLoader />
            </div>
        )
    }

    return (
        <CustomIFrame title="A custom made iframe">
            <CardLinkForm
                cardLinkSessionId={cardLinkSessionId}
                functions={functions}
                hideGiftDetailsButton={hideGiftDetailsButton}
                initialStep={initialStep}
                deleteCard={deleteCard}
                cardToCardUuid={cardToCardUuid}
            />
        </CustomIFrame>
    )
}
